import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API_URL, REST_URL, SERVER_URL } from "settings";
import { loadRemoteComponent } from "components/util/loadRemote";
import { HostControllerContext } from "components/presentation/main/Main.lib.communication";
import { HOST_PATHS } from "components/util/host_routes";
import { usePermissions } from "components/util/helpers/providers/PermissionsProvider";
import ChatPortalManager from "components/presentation/main/Main.sub.ChatPortal.view";
import MenuPortalManager from "components/presentation/main/Main.sub.MenuPortal.view";
import l from "components/util/l";
import { getSolutionBackground, SolutionBackgroundType } from "components/presentation/main/layout/SolutionBackground";

// Mounts the project — one Module Federation app — directly in the host realm. It loads
// the exposed `./app` module from the project's remoteEntry.js, wires the bridge and the
// page's history into the host controller, and hands the app its context through the
// __seedView prop (consumed by SeedViewProvider inside the remote).
//
// The host has no say in what is on screen: the project owns its router and its layout, so
// this component's whole job is the mount, the container, and the theme around it.

const DEFAULT_PRIMARY_COLOR = "#2080e5";
const DEFAULT_ACCENT_COLOR = "#f0b000";
const DEFAULT_EXPOSED_MODULE = "./app";

const AppMount = ({ flow, appView, hostHistory, search, chats, menus, openMenus, setMenuOpen }) => {

  const host = useContext(HostControllerContext);
  const { snapshot: permissionSnapshot } = usePermissions();
  const [Component, setComponent] = useState(null);
  const [error, setError] = useState(null);
  const [containerNode, setContainerNode] = useState(null);
  const subscribersRef = useRef(new Set());

  // The container holds the rendered app; the portal managers fill the placeholders inside
  // it — <Chat> into [data-seed-chat], the side menu into [data-seed-menu] (same document
  // — no CSS syncing).
  const containerCallbackRef = useCallback((node) => setContainerNode(node), []);

  const remoteUrl = appView.url.startsWith("http")
    ? appView.url
    : `${SERVER_URL}${appView.url}`;
  const exposed = appView?.settings?.exposed || DEFAULT_EXPOSED_MODULE;

  // Fan host events out to every hook that subscribed inside the app.
  const dispatch = useMemo(() => (message) => {
    subscribersRef.current.forEach((handler) => handler(message));
  }, []);

  // Register with the host so its controller events (execution, navigation, app storage)
  // reach the app; unregister on unmount.
  useEffect(() => {
    if (!host?.registerApp) return;
    host.registerApp(dispatch);
    return () => host.registerApp(null);
  }, [host, dispatch]);

  const bridge = useMemo(() => ({
    sendAction: (name, args = {}) => {
      host?.sendControllerAction?.({ type: "ACTION", name, ...args });
    },
    subscribe: (handler) => {
      subscribersRef.current.add(handler);
      return () => subscribersRef.current.delete(handler);
    },
  }), [host]);

  // App-global runtime values. The project's data layer resolves them off window and
  // sessionStorage: /settings reads apiUrl, /utils/api reads the token, and the runtime
  // reads flow_id and restUrl. Declared BEFORE the loader effect so they exist by the time the remote
  // evaluates (settings.js captures API_URL at module load).
  useEffect(() => {
    if (typeof window === "undefined") return;

    window.__SEED_VIEW_CONTEXT = {
      ...(window.__SEED_VIEW_CONTEXT || {}),
      apiUrl: API_URL,
      restUrl: REST_URL,
      flow_id: flow?.id,
      theme: JSON.stringify({ ...flow?.theme, bgAlpha: appView.bg_alpha }),
      search: search || "",
      permissions: permissionSnapshot,
    };

    if (API_URL) sessionStorage.setItem("apiUrl", API_URL);
    if (REST_URL) sessionStorage.setItem("restUrl", REST_URL);
    if (flow?.id != null) sessionStorage.setItem("flow_id", String(flow.id));
    // The auth token lives in localStorage; the project runtime reads sessionStorage —
    // the same mirroring Approval.js / Form.js already do.
    const token = localStorage.getItem("token");
    if (token) sessionStorage.setItem("token", token);

    window.dispatchEvent(new CustomEvent("seed:permissions", { detail: permissionSnapshot }));
  }, [flow?.id, flow?.theme, appView.bg_alpha, search, permissionSnapshot]);

  // Theme custom properties, rendered as a <style> in this component's own JSX rather than
  // injected into document.head: a style element applies document-wide wherever it sits,
  // so it still reaches :root, but React owns its lifecycle — switching flows re-renders it
  // and unmounting removes it.
  //
  // It targets :root (not the container) because it must also reach overlays:
  // /helpers/OverlayPortal mounts into document.body, outside this container, and custom
  // properties only inherit there from a common ancestor.
  //
  // Only the seed-specific names. Deliberately NOT --primary / --accent: those belong to
  // the host's Bootstrap theme, and overriding them would retheme the host chrome.
  const primaryColor = flow?.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;
  const accentColor = flow?.theme?.accentColor || DEFAULT_ACCENT_COLOR;
  const themeCss = `:root { --primary-color: ${primaryColor}; --accent-color: ${accentColor}; }`;

  // Load the remote.
  useEffect(() => {
    let cancelled = false;

    setComponent(null);
    setError(null);
    loadRemoteComponent(remoteUrl, exposed)
      .then((Loaded) => { if (!cancelled) setComponent(() => Loaded); })
      .catch((err) => {
        // Surface the real cause: the fallback UI alone makes this undebuggable.
        console.error("[SeedBot] Failed to load the project app", { remoteUrl, exposed, error: err });
        if (!cancelled) setError(err);
      });

    return () => { cancelled = true; };
  }, [remoteUrl, exposed]);

  // The app's router runs on the host's own history — one history for the page, so the
  // host and the app can never disagree on where the browser is. Everything under /app
  // (this router's basename) is the app's route space except `hostPaths`, which the
  // platform kept for its own modals and sections: the app's router skips those instead of
  // redirecting away from a modal that just opened over it.
  const seedView = useMemo(() => ({
    bridge,
    history: hostHistory,
    context: {
      apiUrl: API_URL,
      theme: JSON.stringify({ ...flow?.theme, bgAlpha: appView.bg_alpha }),
      flow_id: flow?.id,
      hostPaths: HOST_PATHS,
      search: search || "",
    },
  }), [bridge, hostHistory, flow, appView.bg_alpha, search]);

  const stripBackground = getSolutionBackground(flow).type === SolutionBackgroundType.SOLID
    && !isHexColor(appView.bg_color);
  const backgroundColor = stripBackground ? "transparent" : getViewBackgroundColor(appView);
  const bgHexColor = isHexColor(appView.bg_color) ? appView.bg_color : "#000000";
  const rawBgAlpha = Number(appView.bg_alpha ?? 0.1);
  const bgAlpha = Number.isFinite(rawBgAlpha) ? rawBgAlpha : 0.1;

  const containerStyle = {
    ...(backgroundColor ? { backgroundColor } : {}),
    "--seed-view-bg-color": bgHexColor,
    "--seed-view-bg-alpha": bgAlpha,
    "--seed-view-bg": backgroundColor || "rgba(0, 0, 0, 0.1)",
  };

  // The section renders inside a flex row over an animated background (see
  // AnimatedContainer), and the layout column that used to sit here said both things the
  // mount now says itself: its width — without it the app is a flex item with no basis and
  // shrinks to whatever screen is on it — and that it is positioned, or the background's
  // absolutely placed spans paint over the app and swallow every click.
  return <div className="custom-view position-relative w-100 h-100">
    <style>{themeCss}</style>
    <div
      ref={containerCallbackRef}
      // The scoping contract with the build: the project's stylesheets have their
      // html/body rules rewritten to this container's scope class, so what used to style a
      // document now styles this element instead of leaking into the host page.
      data-seed-app
      className="seed-view-root section-container section-iframe overflow-auto h-100"
      style={containerStyle}
    >
      {error && <div className="p-3 text-danger small" data-testid="app-mount-error">
        <div>{l("No se pudo cargar la aplicación del proyecto%PA978")}</div>
        <div className="text-muted mt-1">{String(error?.message || error)}</div>
      </div>}
      {!error && Component && <Component __seedView={seedView} />}
    </div>
    <ChatPortalManager
      containerNode={containerNode}
      chats={chats}
      flow={flow}
    />
    <MenuPortalManager
      containerNode={containerNode}
      menus={menus}
      openMenus={openMenus}
      flow={flow}
      setMenuOpen={setMenuOpen}
    />
  </div>;

};

const getViewBackgroundColor = (view) => {
  const bgColor = view?.bg_color;
  if (!isHexColor(bgColor)) return null;

  const bgAlpha = Number(view?.bg_alpha ?? 0.1);
  const alpha = Number.isFinite(bgAlpha) ? Math.max(0, Math.min(bgAlpha, 1)) : 0.1;
  const hex = bgColor.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isHexColor = (value) =>
  typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);

export default AppMount;
